import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovChangePassword = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const forced = params.get('forced') === '1' || (user && user.must_change_password);
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { document.title = 'Change password · USKE Procurement Portal'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
    if (form.new_password !== form.confirm) return toast({ title: "Passwords don't match", variant: 'destructive' });
    if (form.new_password === form.old_password) return toast({ title: 'Pick a different password', variant: 'destructive' });
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast({ title: 'Password updated', description: 'Your new password is now active.' });
      // Clear the flag locally so the layout stops redirecting
      if (user) login({ ...user, must_change_password: false }, localStorage.getItem('token'));
      navigate(portalPath('/dashboard'), { replace: true });
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="gov-change-password-page">
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5 text-blue-700" /> Change password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {forced && (
            <div className="p-3 rounded bg-amber-50 border border-amber-300 text-amber-900 text-sm flex gap-2" data-testid="gov-change-password-forced">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Action required:</strong> Your account uses a temporary password.
                Please choose a new one before continuing.
              </span>
            </div>
          )}
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="op">Current password</Label>
              <Input id="op" type="password" required value={form.old_password} onChange={(e) => setForm({ ...form, old_password: e.target.value })} data-testid="gov-old-password" />
            </div>
            <div>
              <Label htmlFor="np">New password <span className="text-xs text-slate-400">(min 8 chars)</span></Label>
              <Input id="np" type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="gov-new-password" />
            </div>
            <div>
              <Label htmlFor="cp">Confirm new password</Label>
              <Input id="cp" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="gov-confirm-password" />
            </div>
            <div className="flex justify-between items-center pt-2">
              {forced ? (
                <Button type="button" variant="ghost" className="text-rose-600" onClick={async () => { await logout(); navigate(portalPath('/login')); }}>Sign out</Button>
              ) : (
                <Button type="button" variant="ghost" onClick={() => navigate(portalPath('/dashboard'))}>Cancel</Button>
              )}
              <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="gov-change-password-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
                Update password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default GovChangePassword;
